import {useEffect} from 'react';

import {CurrentUser} from 'common/report/queries/app-api/user-metadata-client';
import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import {findCurrentValidWorkspaceId, getCurrentWorkspaceId} from 'iron/routes/global-sidenav/utils';
import {makePathFromUrlState, parseStateFromUrl} from 'common/report/util/state-from-url';
import {getProjectId, isPersistableReport} from 'common/report/util';
import {reporter} from 'common/report/error-reporting';
import {safeSetCookie} from 'common/util/cookie';
import {REPORT_PERSISTENCE_COOKIE, UserFlag} from 'common/report/constants';
import {ProjectsResource} from 'common/report/model-resources/projects';
import {getUserFlags, setUserFlag} from 'common/report/queries/app-api/user-flags';
import {userFlagsStore} from 'common/report/stores/user-flags';
import {promptFYTModal} from 'common/widgets/find-your-team-modal';
import {ReportPersistenceCookie, UrlLocation} from 'iron/routes/global-sidenav/types';
import {useNavManager} from 'common/contexts/react/nav-manager';

export function usePersistLastSeenUrlData({
  location,
  currentProject,
}: {
  location: UrlLocation;
  currentProject: CurrentProject | undefined;
}) {
  useEffect(() => {
    if (!isPersistableReport()) {
      return;
    }

    const reportEndpoint = parseStateFromUrl(location.pathname)?.appName;
    if (!reportEndpoint) {
      reporter.warn(`Report info not found for given URL`, {location});
      return;
    }

    // currentProject must be set before we can get the actual valid workspace id
    if (currentProject) {
      // we need to check the current workspace id is equal to the valid workspace id
      // or we run the risk of setting the wrong workspace id in the cookie
      let workspaceId = getCurrentWorkspaceId({currentProject});
      const validWorkspaceId = findCurrentValidWorkspaceId({currentProject, firstPriorityWorkspaceId: workspaceId});
      if (validWorkspaceId !== workspaceId) {
        workspaceId = validWorkspaceId;
      }
      safeSetCookie(
        REPORT_PERSISTENCE_COOKIE,
        JSON.stringify({
          project: currentProject.id,
          ...(workspaceId ? {workspace: workspaceId} : {}),
          report_path: reportEndpoint,
        } as ReportPersistenceCookie),
        14 /*days*/,
      );
    }
  }, [location]);
}

export function useRedirectInvalidProject({
  currentUser,
  currentProject,
  projectsResource,
  location,
}: {
  currentUser: CurrentUser | undefined;
  currentProject: CurrentProject | undefined;
  projectsResource: ProjectsResource;
  location: UrlLocation;
}) {
  const navManager = useNavManager();

  useEffect(() => {
    const allProjects = projectsResource.getAllValues();
    if (!currentUser || !allProjects) {
      return;
    }

    const isStaff = currentUser.isStaff;
    if (isStaff) {
      return;
    }
    const currentProjectId = parseInt(getProjectId(), 10);
    const currentWorkspaceId = getCurrentWorkspaceId({currentProject});
    const currentProjectItem = allProjects.find((project) => project.id === currentProjectId);
    const currentWorkspace = allProjects
      .map((project) => Object.values(project.workspaces))
      .flat()
      .find((workspace) => workspace.id === currentWorkspaceId);
    // catches the case when the workspace is valid and the current user has access, but the project is invalid
    // (for example, a project that's been reset)
    if (!currentProjectItem && currentWorkspace) {
      const fragment = location.hash;
      const appPath = parseStateFromUrl(location.pathname)?.appPath || ``;
      const path = makePathFromUrlState({
        projectId: currentWorkspace.projectId.toString(),
        workspaceId: currentWorkspace.id.toString(),
      });

      const redirectUrl = path + fragment + appPath;

      console.error(`Invalid project, redirecting to valid project`, {
        oldProjectId: currentProjectId,
        newProjectId: currentWorkspace.projectId,
      });
      mixpanel.track(
        `Invalid project redirect`,
        {
          'Old project': currentProjectId,
          'New project': currentWorkspace.projectId,
        },
        () => {
          navManager.navigate(redirectUrl);
        },
      );
    }
  }, [currentUser, currentProject, projectsResource, location, navManager]);
}

export function useRedirectInvalidWorkspace({
  currentUser,
  currentProject,
  location,
}: {
  currentUser: CurrentUser | undefined;
  currentProject: CurrentProject | undefined;
  location: UrlLocation;
}) {
  const navManager = useNavManager();

  useEffect(() => {
    if (!currentProject) {
      return;
    }

    // If a staff user doesn't have access to any workspaces, we keep the workspace_id that was initialized
    // this prevents workspace_id from being changed as a staff user moves from app to app.
    if (currentUser?.isStaff && !Object.keys(currentProject.workspaces).length) {
      return;
    }
    const reportEndpoint = parseStateFromUrl(location.pathname)?.appName;

    const currentWorkspaceId = getCurrentWorkspaceId({currentProject, reportEndpoint});
    const validWorkspaceId = findCurrentValidWorkspaceId({
      currentProject,
      firstPriorityWorkspaceId: currentWorkspaceId,
    });

    const parsedStateFromUrl = parseStateFromUrl(location.pathname);
    if (validWorkspaceId !== currentWorkspaceId && reportEndpoint) {
      const fragment = location.hash;
      const appPath = parsedStateFromUrl?.appPath || ``;
      const path = makePathFromUrlState({
        projectId: currentProject.id.toString(),
        workspaceId: validWorkspaceId?.toString(),
      });

      const redirectUrl = path + fragment + appPath;

      console.error(`Invalid workspace, redirecting to valid workspace`, {
        oldWorkspaceId: currentWorkspaceId,
        newWorkspaceId: validWorkspaceId,
      });
      mixpanel.track(
        `Invalid workspace redirect`,
        {
          'Old workspace': currentWorkspaceId,
          'New workspace': validWorkspaceId,
        },
        () => {
          navManager.navigate(redirectUrl);
        },
      );
    }
  }, [currentUser, currentProject, location, navManager]);
}

export function useShowFYTModalIfNeeded({
  currentUser,
  currentProject,
}: {
  currentUser: CurrentUser | undefined;
  currentProject: CurrentProject | undefined;
}) {
  useEffect(() => {
    const showFYTModalIfNeeded = async () => {
      if (!currentProject || !currentUser) {
        return;
      }

      const flags = await getUserFlags();
      const showFYTModal =
        !(UserFlag.ViewedFYTModal in flags) &&
        currentUser.organizations?.[currentProject.organizationId]?.should_show_fyt_modal;

      if (showFYTModal) {
        setUserFlag(UserFlag.ViewedFYTModal);
        userFlagsStore.update({[UserFlag.ViewedFYTModal]: 1});
        await promptFYTModal();
      }
    };
    showFYTModalIfNeeded();
  }, [currentProject, currentUser]);
}
